/* eslint-disable max-lines-per-function */
import React, { ChangeEvent, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import { Grid, Typography } from "@material-ui/core";
import { FormikConfig } from "formik";
import logger from "services/logger";
import { userLoginToken } from "utils/general-utils";
import { useSnackbar } from "components/haven-design/snackbar";
import { Viewer } from "components/auth/types";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { Button } from "components/haven-design/buttons";
import { CCEmailForm } from "../property/lists/cc-email-form";

interface Props {
  path: string;
  viewer: Viewer;
}

interface UpdateRes {
  id: string;
  email: string;
}

interface UpdateVars {
  id: string;
  data: { bccEmail: string };
}

interface QueryRes {
  viewer: Viewer;
}

const ORG_QUERY = gql`
  query orgQuery {
    viewer {
      id
      organization {
        id
        bccEmail
        logoFilename
      }
    }
  }
`;

const UPDATE_BCC_EMAIL = gql`
  mutation updateOrgDashboard($id: ID!, $data: OrganizationInput!) {
    updateOrganization(id: $id, data: $data) {
      id
      bccEmail
    }
  }
`;

const BccSettings = (props: { viewer: Viewer; data: QueryRes }) => {
  const defaultEmail = props.data.viewer.organization?.bccEmail || "";
  const { organizationId } = props.viewer;
  if (!organizationId) return null;
  const { openSnackbar } = useSnackbar();

  const [updateBccEmail] = useMutation<UpdateRes, UpdateVars>(UPDATE_BCC_EMAIL);

  const onSubmit: FormikConfig<{email: string}>["onSubmit"] = (values) => {
    updateBccEmail({
      variables: { id: organizationId, data: { bccEmail: values.email.trim() } },
    })
      .then(() => {
        openSnackbar({ message: "Org Email saved", status: "success" });
      })
      .catch((err) => {
        openSnackbar({ message: "Error saving Organization Email", status: "error" });
        logger.sendError(err);
      });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle2">Set automatic Bcc email</Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <CCEmailForm initialEmail={defaultEmail} onSubmit={onSubmit} type="organization" />
      </Grid>
    </Grid>
  );
};

interface UpdateLogoRes {
  id: string;
  logoFilename: string;
}

interface UpdateLogoVars {
  id: string;
  data: { logoFilename: string };
}

const UPDATE_LOGO = gql`
  mutation updateOrgLogo($id: ID!, $data: OrganizationInput!) {
    updateOrganization(id: $id, data: $data) {
      id
    }
  }
`;

// eslint-disable-next-line max-lines-per-function
const OrgLogoSettings = (props: { data: QueryRes; onUpload: () => void }) => {
  const [uploading, setUploading] = useState<boolean>(false);

  const [updateLogo] = useMutation<UpdateLogoRes, UpdateLogoVars>(UPDATE_LOGO);

  const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];

    if (file) {
      const token = userLoginToken();
      const formData = new FormData();
      formData.append("logo", file as Blob);
      setUploading(true);
      fetch("/uploads/upload-org-logo", {
        body: formData,
        method: "POST",
        headers: { authorization: `Bearer ${token}` },
      })
        .then(() => {
          setUploading(false);
          props.onUpload();
        })
        .catch((err) => {
          setUploading(false);
          logger.sendError(err);
        });
    }
  };

  const deleteHandler = () => {
    if (
      // eslint-disable-next-line no-alert
      !confirm("Are you sure you want to delete your current logo?. This action can not be undone.")
    ) {
      return;
    }
    updateLogo({
      variables: {
        id: props?.data?.viewer?.organization?.id || "",
        data: { logoFilename: "" },
      },
    }).then(() => props.onUpload());
  };

  const logoPath = props.data.viewer?.organization?.logoFilename;

  return (
    <div>
      <h4>Logo</h4>
      <p>Recommended size: 150px x 150px</p>
      <div>
        {logoPath && <img src={logoPath} width="150px" height="150px" />}
        {!logoPath && <p>No logo available</p>}
      </div>
      <div style={{ marginTop: 5 }}>
        <input type="file" name="org-logo" onChange={changeHandler} disabled={uploading} />
        {logoPath && (
          <Button
            style={{ marginTop: 5 }}
            color="secondary"
            onClick={deleteHandler}
            disabled={uploading}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

const OrgSettingsPage: React.FC<Props> = (props: Props) => {
  const { loading, data, error, refetch } = useQuery<QueryRes>(ORG_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;

  return (
    <div>
      <div>
        <h2>Organization Settings</h2>
        <BccSettings viewer={props.viewer} data={data} />
        <OrgLogoSettings data={data} onUpload={refetch} />
      </div>
    </div>
  );
};

export default OrgSettingsPage;
